import { auth } from '@geo-admin/shared/contract/api/index.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AuthCard, AuthShell, authField, Em, HeroPanel, ValueList } from '../components/AuthShell.tsx';
import { ArrowRightIcon } from '../components/icons.tsx';
import { LogoMark } from '../components/LogoMark.tsx';
import { SupportContact } from '../components/SupportContact.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { FieldError, Input, Label } from '../components/ui/form.tsx';
import { call } from '../lib/call.ts';
import { SHELL, WHITE_LABEL } from '../lib/branding.ts';
import { useAfterLogin, useMe } from '../lib/me.ts';
import { pathsFor } from '../lib/paths.ts';

const LoginForm = auth.login.body;

/**
 * 두 로그인 화면(사용자·운영자)이 같은 로직을 쓴다. 요청은 그 화면의 영역 헤더를 싣고, 서버는 **그 영역에 소속이 있는
 * 계정만** 로그인시킨다 — 운영자가 사용자 화면에서 로그인해도 운영자 화면으로 보내 주지 않는다(261010-agency-console)
 */
function useLoginForm() {
  const afterLogin = useAfterLogin();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<z.infer<typeof LoginForm>>({ resolver: zodResolver(LoginForm) });
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const { me } = await call(auth.login, { body: values });
      await afterLogin(me);
    } catch (e) {
      setError((e as Error).message);
    }
  });
  return { form, submit, error };
}

/** "AI 검색에 인용되는 브랜드를 만듭니다" — 앞의 "AI 검색" 을 강조한다 */
function Tagline() {
  const lead = 'AI 검색';
  return SHELL.tagline.startsWith(lead) ? (
    <>
      <Em>{lead}</Em>
      {SHELL.tagline.slice(lead.length)}
    </>
  ) : (
    SHELL.tagline
  );
}

export function LoginPage() {
  const { form, submit, error } = useLoginForm();
  return (
    <AuthShell
      aside={
        <HeroPanel eyebrow={SHELL.descriptor} title={<Tagline />}>
          <ValueList />
        </HeroPanel>
      }
    >
      <AuthCard title="로그인" description="가입하신 이메일과 비밀번호를 입력해 주세요.">
        <form className="grid gap-4" onSubmit={submit}>
          <Label>
            이메일
            <Input type="email" autoComplete="username" className={authField} {...form.register('email')} />
          </Label>
          <Label>
            비밀번호
            <Input
              type="password"
              autoComplete="current-password"
              className={authField}
              {...form.register('password')}
            />
          </Label>
          <FieldError>{error}</FieldError>
          <Button type="submit" variant="live" size="lg" className="mt-1 w-full" disabled={form.formState.isSubmitting}>
            로그인
          </Button>
        </form>
      </AuthCard>
      {/* 화이트라벨에는 셀프 가입이 없다 — 대행사 직원의 초대로만 들어온다(261025-agency-white-label) */}
      {!WHITE_LABEL && (
        <Link
          to="/signup"
          className="group flex items-center justify-between rounded-2xl bg-white px-5 py-4 ring-1 ring-slate-200/70 transition hover:-translate-y-px hover:ring-live/40"
        >
          <span>
            <span className="block text-slate-500 text-sm">처음이신가요?</span>
            <span className="block font-bold text-slate-900">가입 신청하기</span>
          </span>
          <ArrowRightIcon className="h-5 w-5 text-live transition group-hover:translate-x-0.5" />
        </Link>
      )}
      <SupportContact />
    </AuthShell>
  );
}

/** 운영자 로그인 — 브랜드 히어로 없이 간결한 콘솔 화면. 로그인 로직은 고객 화면과 같다 */
export function AdminLoginPage() {
  const { form, submit, error } = useLoginForm();
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2">
          <LogoMark tone="onDark" />
          <span className="rounded bg-live/25 px-1.5 py-0.5 font-semibold text-blue-200 text-xs">운영자</span>
        </div>
        <form className="grid gap-3 rounded-xl bg-white p-6 shadow-2xl" onSubmit={submit}>
          <Label>
            이메일
            <Input type="email" autoComplete="username" {...form.register('email')} />
          </Label>
          <Label>
            비밀번호
            <Input type="password" autoComplete="current-password" {...form.register('password')} />
          </Label>
          <FieldError>{error}</FieldError>
          <Button type="submit" disabled={form.formState.isSubmitting}>
            로그인
          </Button>
        </form>
      </div>
    </div>
  );
}

const PasswordForm = auth.changePassword.body
  .extend({ confirm: z.string() })
  .refine((v) => v.confirm === v.newPassword, { message: '두 입력이 다르다', path: ['confirm'] });

export function PasswordPage() {
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<z.infer<typeof PasswordForm>>({ resolver: zodResolver(PasswordForm) });
  const submit = form.handleSubmit(async ({ currentPassword, newPassword }) => {
    setError(null);
    try {
      await call(auth.changePassword, { body: { currentPassword, newPassword } });
      await qc.invalidateQueries();
      await navigate({ to: pathsFor(me.data?.role).home });
    } catch (e) {
      setError((e as Error).message);
    }
  });
  return (
    <Card className="mx-auto max-w-md">
      <CardHeader
        title="비밀번호 변경"
        description={
          me.data?.mustChangePassword
            ? '임시 비밀번호로 로그인하셨습니다. 새 비밀번호를 정해야 계속 이용하실 수 있습니다'
            : '비밀번호를 바꾸면 다른 기기의 로그인은 모두 해제됩니다'
        }
      />
      <CardBody>
        <form className="grid gap-3" onSubmit={submit}>
          <Label>
            현재 비밀번호
            <Input type="password" autoComplete="current-password" {...form.register('currentPassword')} />
          </Label>
          <Label>
            새 비밀번호 (8자 이상)
            <Input type="password" autoComplete="new-password" {...form.register('newPassword')} />
            <FieldError>{form.formState.errors.newPassword?.message}</FieldError>
          </Label>
          <Label>
            새 비밀번호 확인
            <Input type="password" autoComplete="new-password" {...form.register('confirm')} />
            <FieldError>{form.formState.errors.confirm?.message}</FieldError>
          </Label>
          <FieldError>{error}</FieldError>
          <Button type="submit" disabled={form.formState.isSubmitting}>
            변경
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
